import { ToastEventBus, ToastMessageOptions } from 'primevue'
import { useLocalStorage } from '@vueuse/core'

const defaultToastOptions: ToastMessageOptions = {
  life: 5000,
  closable: true,
}

const getToastRef = () => {
  const toastRef = useLocalStorage<Date[]>('toastCount', [])

  const now = new Date()

  toastRef.value = toastRef.value.filter((date) => {
    return new Date(date).getTime() > now.getTime() - 60 * 1000
  })

  return toastRef
}

const incrementToastCount = () => {
  const toastRef = getToastRef()

  toastRef.value.push(new Date())
}

const add = (options: ToastMessageOptions) => {
  if (getToastRef().value.length > 5) {
    return
  }

  ToastEventBus.emit('add', options)

  incrementToastCount()
}

type ToastSeverity = 'error' | 'success' | 'warn' | 'info'

type ToastFunction = (detail: string, options?: ToastMessageOptions) => void

/**
 * Creates a toast function with the specified severity and translation key
 */
const createToastFunction = (
  t: ReturnType<typeof useI18n>,
  severity: ToastSeverity,
  translationKey: string,
): ToastFunction => {
  return (detail: string, options?: ToastMessageOptions) => add({
    summary: t.t(translationKey),
    detail,
    severity,
    ...defaultToastOptions,
    ...options,
  })
}

export const useToast = (): Record<ToastSeverity, ToastFunction> => {
  const t = useI18n() as ReturnType<typeof useI18n>

  return {
    error: createToastFunction(t, 'error', 'toast.error.summary'),
    success: createToastFunction(t, 'success', 'toast.success.summary'),
    warn: createToastFunction(t, 'warn', 'toast.warn.summary'),
    info: createToastFunction(t, 'info', 'toast.info.summary'),
  }
}
